import React from 'react';
import EntityMultiSelect, { selectedEntityIds } from '../../../shared/components/EntityMultiSelect';
import FormFieldLabel from '../../../shared/components/FormFieldLabel';
import IconAction from '../../../shared/components/IconAction';
import { AssetIcon } from '../../../shared/assets';
import { calendarFormSchema } from '../config/calendarForm.schema';
import { getCalendarEventForm } from '../config/calendarEventTypes';
import { getEventTypeColor } from '../utils/calendar.helpers';
import ClientCustomFieldsSection from '../../clients/components/ClientCustomFieldsSection';
import { archiveCalendarCustomField, createCalendarCustomField, listCalendarCustomFields } from '../services/calendar-custom-fields.service';
import { CreateFormCancelButton, CreateFormDialog, CreateFormSubmitButton } from '../../../shared/components/CreateFormDialog';
import '../styles/calendar-event-form.css';

const CALENDAR_CUSTOM_FIELDS_API = {
  list: listCalendarCustomFields, create: createCalendarCustomField, archive: archiveCalendarCustomField,
};
const FIELD_IDS = { clientId: 'client', supplierId: 'supplier', endDate: 'end-date', durationCustom: 'duration-custom' };
const PRIORITIES = [{ value: 'bassa', label: 'Bassa' }, { value: 'media', label: 'Media' }, { value: 'alta', label: 'Alta' }];

export default function CreateEventDialog({
  open, onOpenChange, mode = 'create', newEvent, eventTypes, staff = [], clients = [], suppliers = [],
  formErrors = {}, isSubmitting = false, onFieldChange, onSubmit, onBack, comments,
}) {
  const isEditMode = mode === 'edit';
  const category = getCalendarEventForm(newEvent.type);
  const EventIcon = ({ size }) => <span className={`calendar-event-form__icon ${getEventTypeColor(newEvent.type)}`}>
    <AssetIcon name={category.iconKey} size={size} />
  </span>;

  function renderField(name) {
    const id = `calendar-${FIELD_IDS[name] || name}`;
    const error = formErrors[name];
    const props = {
      id, value: newEvent[name] ?? '', disabled: isSubmitting,
      onChange: (event) => onFieldChange(name, event.target.value),
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? `${id}-error` : undefined,
    };
    let control;
    if (name === 'attendees') {
      control = <EntityMultiSelect id={id} label="Personale" options={staff} value={newEvent.attendees || []}
        disabled={isSubmitting} onChange={(ids) => onFieldChange('attendees', ids)} />;
    } else if (name === 'clientId' || name === 'supplierId') {
      const kind = name === 'clientId' ? 'client' : 'supplier';
      control = <EntityMultiSelect id={id} label={kind === 'client' ? 'Clienti' : 'Fornitori'}
        options={kind === 'client' ? clients : suppliers} value={selectedEntityIds(newEvent, kind)} disabled={isSubmitting}
        onChange={(ids) => { onFieldChange(`${kind}Ids`, ids); onFieldChange(name, ids[0] || ''); }} />;
    } else if (name === 'description') {
      control = <textarea {...props} rows={3} placeholder={category.descriptionPlaceholder} />;
    } else if (name === 'duration') {
      control = <select {...props}>
        <option value="30m">30 min</option><option value="1h">1 ora</option>
        <option value="2h">2 ore</option><option value="4h">4 ore</option>
        <option value="custom">Personalizzata</option>
      </select>;
    } else if (name === 'amount') {
      control = <div className="calendar-event-form__amount">
        <input {...props} type="text" inputMode="decimal" placeholder="0,00" />
        <span aria-hidden="true">€</span>
      </div>;
    } else {
      const type = name === 'date' || name === 'endDate' ? 'date' : name === 'time' ? 'time' : 'text';
      control = <input {...props} type={type} required={['title', 'date', 'time', 'durationCustom'].includes(name)}
        min={name === 'endDate' ? newEvent.date || undefined : undefined}
        placeholder={name === 'title' ? category.titlePlaceholder : name === 'location'
          ? category.locationPlaceholder || 'Es. Google Meet, sala riunioni…' : name === 'durationCustom' ? 'Es. 1h 45m' : undefined} />;
    }
    return <div key={name} data-form-field={name} className="calendar-event-form__field">
      <FormFieldLabel schema={calendarFormSchema} name={name} htmlFor={id} />
      {control}
      {error ? <p role="alert" id={`${id}-error`} className="wt-inline-error">{error}</p> : null}
    </div>;
  }

  return <CreateFormDialog busy={isSubmitting} open={open} onOpenChange={onOpenChange} icon={EventIcon}
    className="calendar-event-form-dialog"
    title={`${isEditMode ? 'Modifica evento' : 'Nuovo evento'} · ${category.label}`}
    description={category.description}
    footer={<>
      {!isEditMode && onBack ? <IconAction type="button" iconKey="actions.reset" label="Cambia tipo evento" onClick={onBack} disabled={isSubmitting} /> : null}
      <CreateFormCancelButton onClick={() => onOpenChange(false)} disabled={isSubmitting} />
      <CreateFormSubmitButton saving={isSubmitting} form="calendar-event-form">
        {isEditMode ? 'Salva modifiche' : 'Salva evento'}
      </CreateFormSubmitButton>
    </>}
  >
    {formErrors.submit ? <p role="alert" className="wt-inline-error">{formErrors.submit}</p> : null}
    <form id="calendar-event-form" onSubmit={onSubmit} className="create-form-dialog__form calendar-event-form">
      {isEditMode ? <div className="create-form-dialog__full">
        <FormFieldLabel schema={calendarFormSchema} name="type" htmlFor="calendar-type" />
        <select id="calendar-type" value={newEvent.type} disabled={isSubmitting} onChange={(event) => onFieldChange('type', event.target.value)}>
          {eventTypes.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div> : null}
      {renderField('title')}
      {renderField('description')}
      {category.priority ? <fieldset className="calendar-event-form__priority create-form-dialog__full">
        <legend>Priorità</legend>
        <div className="calendar-event-form__priority-options">
          {PRIORITIES.map(({ value, label }) => <label key={value}>
            <input type="radio" name="calendar-priority" value={value} checked={newEvent.priority === value}
              disabled={isSubmitting} onChange={() => onFieldChange('priority', value)} />
            <span>{label}</span>
          </label>)}
        </div>
      </fieldset> : null}
      {category.rows.map((fields, index) => <React.Fragment key={index}>
        <div className={`calendar-event-form__row${fields.length === 1 ? ' calendar-event-form__row--full' : ''}`}>
          {fields.map(renderField)}
        </div>
        {fields.includes('duration') && newEvent.duration === 'custom'
          ? <div className="create-form-dialog__full">{renderField('durationCustom')}</div> : null}
      </React.Fragment>)}
      <ClientCustomFieldsSection formKey={calendarFormSchema.id} values={newEvent.customFields || {}}
        onChange={(customFields) => onFieldChange('customFields', customFields)} api={CALENDAR_CUSTOM_FIELDS_API} />
    </form>
    {isEditMode && comments}
  </CreateFormDialog>;
}
